/**
 * Bookings tab — incoming MS Bookings appointments for the FSR's branch.
 *
 * If the branch's `bookingsBusinessId` is unconfigured, shows a friendly
 * pointer to the Config tab (Manager+) instead of an error.
 */

import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import { Pill, Banner } from '@/components/primitives';
import { AppointmentCard } from '../components/AppointmentCard';
import { useBranchBookings } from '../hooks/useBranchBookings';

export function BookingsTab(): JSX.Element {
  const { user } = useAuth();
  const { config } = useConfig();
  const { appointments, loading, configured } = useBranchBookings();

  const branchCode = user?.activeBranchCode ?? user?.branchCode ?? '';
  const branchName = config.branches.find((b) => b.branchCode === branchCode)?.branchName ?? branchCode;

  return (
    <section className="appt-tab" aria-label="Incoming Bookings">
      <div className="appt-tab__bar">
        <div>
          <h2>Bookings · {branchName || 'your branch'}</h2>
          <span className="appt-tab__bar-sub">Customer self-booked appointments via MS Bookings.</span>
        </div>
      </div>

      {!configured && (
        <Banner tone="info">
          <strong>Bookings business not configured for this branch yet.</strong>{' '}
          A Manager can set the Bookings business id from the <em>Config</em> tab so
          customer-self-booked appointments flow into this view.
        </Banner>
      )}

      {loading && <div className="appt-tab__skeleton" role="status">Loading branch bookings…</div>}

      {!loading && appointments.length === 0 && configured && (
        <div className="appt-empty">
          <Pill variant="muted">No Bookings appointments in the next 7 days</Pill>
          <p>
            Share your branch's public Bookings link with customers to let them self-schedule
            inside or outside business hours.
          </p>
        </div>
      )}

      {!loading && appointments.length > 0 && (
        <ul className="appt-card-list">
          {appointments.map((a) => (
            <li key={a.id}>
              <AppointmentCard appointment={a} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
